import React from 'react';
import { LegalLayout, Section, P, UL, LI, B } from '../components/LegalLayout';

const Privacy = () => (
  <LegalLayout
    title="Privacy Policy"
    effectiveDate="May 28, 2026"
    seoTitle="Privacy Policy, CoupleIn"
    seoDescription="How CoupleIn collects, uses, shares, and protects your personal data. UK GDPR and CCPA/CPRA compliant."
    path="/privacy"
  >
    <Section number="1" title="Who we are">
      <P>
        CoupleIn ("CoupleIn", "we", "us", "our") is operated by <B>Couple In LTD</B>, a company registered in England and Wales (company number 16342045), with its registered office at 54 Dunloe Street, London E2 8EN, United Kingdom.
      </P>
      <P>
        We are the data controller of personal information you provide through the CoupleIn mobile and web app (the "App"). Contact us at <a href="mailto:info@coupleinapp.com" className="text-pink-600 underline">info@coupleinapp.com</a>.
      </P>
    </Section>

    <Section number="2" title="Information we collect">
      <UL>
        <LI><B>Account data:</B> name, email address, profile picture, authentication identifier from your sign-in provider (Google or Apple).</LI>
        <LI><B>Relationship data:</B> partner connection code, partner identifier, relationship start date.</LI>
        <LI><B>Content you create:</B> daily mood entries, journal sentences, calendar events, gestures, conflict-resolution session metadata, browny points history.</LI>
        <LI><B>Device &amp; usage data:</B> device platform (iOS/Android/Web), device push token (FCM), timezone offset, last-active timestamps, anonymous in-app event logs used for product analytics.</LI>
        <LI><B>Subscription data:</B> trial status, subscription tier (handled via Apple/Google in-app purchase, we never see your payment card).</LI>
      </UL>
      <P>
        We do <B>not</B> collect biometric data, precise location, contacts, photos beyond the profile picture you upload, microphone or camera input, health data, or any special category data under UK GDPR.
      </P>
    </Section>

    <Section number="3" title="Why we use your data (legal bases)">
      <UL>
        <LI><B>Contractual necessity</B> (UK GDPR Art. 6(1)(b)): to provide the App features you signed up for, partner connection, mood sync, calendar, gestures, points, conflict resolution.</LI>
        <LI><B>Legitimate interests</B> (Art. 6(1)(f)): to keep the App secure, prevent abuse, improve reliability and measure non-identifying product usage.</LI>
        <LI><B>Consent</B> (Art. 6(1)(a)): to send you push notifications (you control these in Settings → Notifications).</LI>
        <LI><B>Legal obligation</B> (Art. 6(1)(c)): to comply with valid law-enforcement requests and tax/accounting requirements.</LI>
      </UL>
    </Section>

    <Section number="4" title="Sharing data with your partner">
      <P>
        CoupleIn is a two-person app. When you connect with a partner, the following becomes visible to them: your first name, profile picture, mood entries from each day they have access to view, calendar events you mark as shared, gestures and points you exchange, and conflict-resolution sessions you initiate together.
      </P>
      <P>
        When you disconnect a partner or delete your account, your partner's access to your shared data is removed. Each user can also delete their account at any time, which removes their personal data from our systems.
      </P>
    </Section>

    <Section number="5" title="Service providers (processors)">
      <P>We share data with vetted processors who act only on our instructions and under written contracts:</P>
      <UL>
        <LI><B>Google Firebase Cloud Messaging</B>, delivers push notifications. Receives your device token and notification payload.</LI>
        <LI><B>Google Sign-In / Apple Sign In</B>, authenticates you. Receives the email/identifier you grant.</LI>
        <LI><B>MongoDB Atlas (AWS Europe region)</B>, stores your app data at rest, encrypted.</LI>
        <LI><B>Google AdMob</B> (non-premium users only), serves a single interstitial ad per day before partner mood reveal. AdMob may collect advertising identifiers; you can opt out via your device's OS settings (iOS: Settings → Privacy → Tracking; Android: Settings → Google → Ads → Reset/limit advertising ID).</LI>
        <LI><B>Apple App Store / Google Play</B>, handles all payments for premium subscriptions. We receive a confirmation only.</LI>
        <LI><B>Email infrastructure</B>, for transactional and support emails.</LI>
      </UL>
      <P>We do <B>not</B> sell your personal data. We do <B>not</B> share data for cross-context behavioural advertising.</P>
    </Section>

    <Section number="6" title="International transfers">
      <P>
        Our primary servers are hosted in the European Economic Area. Where any processor is based outside the EEA/UK (e.g. Google in the United States), transfers rely on the UK International Data Transfer Addendum to the EU Standard Contractual Clauses, or an equivalent UK adequacy mechanism.
      </P>
    </Section>

    <Section number="7" title="How long we keep data">
      <UL>
        <LI><B>Active account data:</B> kept while your account is active.</LI>
        <LI><B>Notifications history:</B> auto-deleted after 90 days (TTL index).</LI>
        <LI><B>Ad-watch records:</B> auto-deleted after 30 days.</LI>
        <LI><B>Account deletion:</B> when you delete your account in-app, all personal data is removed from production within 30 days. Anonymised aggregate statistics may be retained.</LI>
        <LI><B>Backups:</B> encrypted backups roll off within 35 days.</LI>
      </UL>
    </Section>

    <Section number="8" title="Your rights">
      <P>Under UK GDPR you can request to:</P>
      <UL>
        <LI>access a copy of your data,</LI>
        <LI>correct inaccurate data,</LI>
        <LI>delete your data (also available directly in Settings → Delete Profile),</LI>
        <LI>restrict or object to processing,</LI>
        <LI>port your data to another service,</LI>
        <LI>withdraw consent at any time.</LI>
      </UL>
      <P>
        California residents (CCPA/CPRA) have parallel rights: right to know, delete, correct, opt out of sale/sharing (we do not sell or share your data), and non-discrimination. We treat verified California requests the same way as UK GDPR requests.
      </P>
      <P>
        To exercise any right, email <a href="mailto:info@coupleinapp.com" className="text-pink-600 underline">info@coupleinapp.com</a>. We will respond within 30 days. You may also lodge a complaint with the UK Information Commissioner's Office (<a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-pink-600 underline">ico.org.uk</a>).
      </P>
    </Section>

    <Section number="9" title="Security">
      <P>
        We use industry-standard safeguards: TLS 1.2+ in transit, AES-256 encryption at rest, OAuth-based authentication, least-privilege access controls, and rotating credentials. No system is 100% secure; if a personal-data breach occurs we will notify affected users and the ICO within 72 hours where required.
      </P>
    </Section>

    <Section number="10" title="Children's privacy">
      <P>
        The App is intended for users aged 17 and over. We do not knowingly collect data from children under 17. If you believe a child has provided us data, contact <a href="mailto:info@coupleinapp.com" className="text-pink-600 underline">info@coupleinapp.com</a> and we will delete it.
      </P>
    </Section>

    <Section number="11" title="Push notifications">
      <P>
        Push notifications are sent only with your operating-system-level permission. You can disable categories granularly in Settings → Notifications, or globally via your device settings. Mood-related notifications are core to the product and remain on; you can still mute pushes by toggling the master switch.
      </P>
    </Section>

    <Section number="12" title="Apple App Tracking Transparency (ATT)">
      <P>
        We do not track you across other companies' apps and websites for advertising purposes. Our integrated ad partner (Google AdMob) is configured to serve non-personalised or limited-tracking ads in line with your ATT response on iOS.
      </P>
    </Section>

    <Section number="13" title="Changes to this policy">
      <P>
        If we make material changes we will notify you in-app and via email where reasonable, at least 7 days before they take effect. Continued use after the effective date means you accept the updated policy.
      </P>
    </Section>

    <Section number="14" title="Contact">
      <P>
        <B>Couple In LTD</B><br />
        54 Dunloe Street, London E2 8EN, United Kingdom<br />
        Email: <a href="mailto:info@coupleinapp.com" className="text-pink-600 underline">info@coupleinapp.com</a>
      </P>
    </Section>
  </LegalLayout>
);

export default Privacy;
